// FacePath.js
import React, { useEffect, useRef, useContext } from "react";
import { LMDrawingContext } from '../contexts/AppContext';

function FacePath({ selectedShot, selectedSetup }) {
  const canvasRef = useRef(null);
  const { lmDrawing } = useContext(LMDrawingContext);
  const imageCache = {};

  function getCachedImage(src) {
    // if we've already created an Image for this src, reuse it
    if (imageCache[src]) {
      return imageCache[src];
    }

    // otherwise create it once, store it, and return it
    const img = new Image();
    img.src = src;
    imageCache[src] = img;
    return img;
  }

  function getTopViewHeadImage(setup) {
    const clubCategory = (setup?.ClubCategory || '').toLowerCase();
    const clubDescription = (setup?.ClubDescription || '').toLowerCase();
    const pcCategoryId = setup?.PCCategoryId;

    if (clubCategory.includes('hybrid') || pcCategoryId === "13") {
      return "/heads/hybridtop.png";
    }

    if (clubCategory.includes('wedge') || pcCategoryId === "4") {
      return "/heads/wedgetop.png";
    }

    if (clubCategory.includes('iron') || pcCategoryId === "2") {
      return "/heads/irontop.png";
    }

    if (
      clubCategory.includes('fairway') ||
      (clubCategory.includes('wood') && !clubCategory.includes('driver')) ||
      clubDescription.includes('wd')
    ) {
      return "/heads/fairwaytop.png";
    }

    return "/heads/top.png";
  }

  useEffect(() => {
    const canvas2 = canvasRef.current;
    if (!canvas2) return;

    let face = 0;
    let path = 0;
    let hasFacePath = false;

    if (selectedShot) {
      hasFacePath =
        selectedShot.LMInfos?.some(item => {
          if (item.LMType === lmDrawing &&
            "ImpactYawAngleDeg" in item.Measurements &&
            "ImpactPathAngleDeg" in item.Measurements
          ) {
            face = item.Measurements["ImpactYawAngleDeg"];
            path = item.Measurements["ImpactPathAngleDeg"];
            return true;
          }
          else {
            face = 0;
            path = 0;
            return false;
          }
        }
        );
    } else {
      hasFacePath = false;
    }

    const ctx2 = canvas2.getContext("2d");
    const centerX2 = canvas2.width / 2;
    const centerY2 = canvas2.height / 2;

    function drawArrowhead2(x, y, angle, color) {
      const size = 15;
      ctx2.fillStyle = color;
      ctx2.beginPath();
      ctx2.moveTo(x - 2, y);
      ctx2.lineTo(
        x - size * Math.cos(angle - Math.PI / 6),
        y - size * Math.sin(angle - Math.PI / 6)
      );
      ctx2.lineTo(
        x - size * Math.cos(angle + Math.PI / 6),
        y - size * Math.sin(angle + Math.PI / 6)
      );
      ctx2.closePath();
      ctx2.fill();
    }

    function drawLine2(angle, length, color, dashed = false, start = false, end = false) {
      ctx2.beginPath();
      ctx2.moveTo(centerX2, centerY2);
      const radians2 = angle;
      const endX2 = centerX2 + length * Math.cos(radians2);
      const endY2 = centerY2 + length * Math.sin(radians2);

      if (dashed) {
        ctx2.setLineDash([5, 5]);
      } else {
        ctx2.setLineDash([]);
      }

      ctx2.lineTo(endX2, endY2);
      ctx2.strokeStyle = color;
      ctx2.lineWidth = 2;
      ctx2.stroke();

      if (start) {
        drawArrowhead2(endX2, endY2, radians2, color);
      }
      if (end) {
        drawArrowhead2(centerX2, centerY2, radians2 + Math.PI, color);
      }
    }

    function drawPoint2(point, color) {
      ctx2.fillStyle = color;
      ctx2.beginPath();
      ctx2.arc(point.x, point.y, 5, 0, 2 * Math.PI);
      ctx2.fill();
    }

    const faceDeg = face;
    const pathDeg = path;

    const FaceAngle = faceDeg * (Math.PI / 180) + Math.PI;
    const PathAngle = pathDeg * (Math.PI / 180);

    const Faceangle1_2 = FaceAngle;
    const Pathangle2_2 = PathAngle;

    const angleText_1 = `${(faceDeg).toFixed(1)}° Face`;
    const angleText2_2 = `${(pathDeg).toFixed(1)}° Path`;

    const radius2 = 120;
    const width2 = 170;
    const height2 = 200;

    let FaceStartAngle_2, FaceEndAngle_2, FaceDir;
    if (Faceangle1_2 <= Math.PI) {
      FaceStartAngle_2 = Faceangle1_2;
      FaceEndAngle_2 = Math.PI;
      FaceDir = false;
    } else {
      FaceStartAngle_2 = Faceangle1_2;
      FaceEndAngle_2 = Math.PI;
      FaceDir = true;
    }

    let PathStartAngle_2, PathEndAngle_2, PathDir;
    if (Pathangle2_2 <= 0) {
      PathStartAngle_2 = Pathangle2_2;
      PathEndAngle_2 = 0;
      PathDir = false;
    } else {
      PathStartAngle_2 = Pathangle2_2;
      PathEndAngle_2 = 0;
      PathDir = true;
    }

    let FtPstartAngle, FtPendAngle, FtPDir = true;
    if (Pathangle2_2 + Math.PI > Faceangle1_2) {
      FtPstartAngle = Pathangle2_2 + Math.PI;
      FtPendAngle = Faceangle1_2;
    } else {
      FtPendAngle = Pathangle2_2 + Math.PI;
      FtPstartAngle = Faceangle1_2;
    }

    const lighterLightBlue2 = "hsl(195, 53%, 90%, 0.2)";
    const lighterLightPink2 = "rgba(255, 228, 232, 0.2)";
    const lighterLightOrange = "hsla(30, 100%, 80%, 0.2)";

    const textX_2 =
      centerX2 +
      (radius2 + 20) * Math.cos((FaceStartAngle_2 + FaceEndAngle_2) / 2) -
      40;
    const textY_2 =
      centerY2 +
      (radius2 + 30) * Math.sin((FaceStartAngle_2 + FaceEndAngle_2) / 2);

    const clubImage = getTopViewHeadImage(selectedSetup);
    const img2 = getCachedImage(clubImage);
    img2.onload = () => {
      ctx2.clearRect(0, 0, canvas2.width, canvas2.height);

      // Rotated top-view club image
      ctx2.save();
      ctx2.translate(centerX2 - width2 / 2 + 43, centerY2);
      drawPoint2({ x: 2, y: 2 }, "orange");
      ctx2.rotate(Faceangle1_2 + Math.PI);
      ctx2.drawImage(img2, 0, -height2 / 2, width2, height2);
      ctx2.restore();

      if (hasFacePath) {
        ctx2.textAlign = "left";
        // Face (blue) arc
        ctx2.beginPath();
        ctx2.arc(
          centerX2,
          centerY2,
          radius2,
          FaceStartAngle_2,
          FaceEndAngle_2,
          FaceDir
        );
        ctx2.strokeStyle = "cyan";
        ctx2.lineWidth = 2;
        ctx2.stroke();

        ctx2.beginPath();
        ctx2.moveTo(centerX2, centerY2);
        ctx2.arc(
          centerX2,
          centerY2,
          radius2,
          FaceStartAngle_2,
          FaceEndAngle_2,
          FaceDir
        );
        ctx2.lineTo(centerX2, centerY2);
        ctx2.closePath();
        ctx2.fillStyle = lighterLightBlue2;
        ctx2.fill();

        ctx2.fillStyle = "cyan";
        ctx2.font = "bold 18px 'Comfortaa'";
        ctx2.strokeStyle = "#000";
        ctx2.lineWidth = 3;
        ctx2.strokeText(angleText_1, textX_2, textY_2 + 60);
        ctx2.fillText(angleText_1, textX_2, textY_2 + 60);

        // Path (magenta) arc
        ctx2.beginPath();
        ctx2.arc(
          centerX2,
          centerY2,
          radius2,
          PathStartAngle_2,
          PathEndAngle_2,
          PathDir
        );
        ctx2.strokeStyle = "magenta";
        ctx2.lineWidth = 2;
        ctx2.stroke();

        const textX2_2 =
          centerX2 +
          (radius2 + 10) *
          Math.cos((PathStartAngle_2 + PathEndAngle_2) / 2) +
          60;
        const textY2_2 =
          centerY2 +
          (radius2 + 10) *
          Math.sin((PathStartAngle_2 + PathEndAngle_2) / 2) + 30;

        ctx2.beginPath();
        ctx2.moveTo(centerX2, centerY2);
        ctx2.arc(
          centerX2,
          centerY2,
          radius2,
          PathStartAngle_2,
          PathEndAngle_2,
          PathDir
        );
        ctx2.lineTo(centerX2, centerY2);
        ctx2.closePath();
        ctx2.fillStyle = lighterLightPink2;
        ctx2.fill();

        ctx2.fillStyle = "magenta";
        ctx2.font = "bold 18px 'Comfortaa'";
        ctx2.strokeStyle = "#000";
        ctx2.lineWidth = 3;
        ctx2.textAlign = 'right';
        ctx2.strokeText(angleText2_2, textX2_2, textY2_2);
        ctx2.fillText(angleText2_2, textX2_2, textY2_2);

        // Baselines
        drawLine2(0, 200, "orange", true, false, false);
        drawLine2(Math.PI, 200, "orange", true, false, false);

        // Face & path vectors
        drawLine2(Faceangle1_2, 200, "cyan", false, true, false);
        drawLine2(Pathangle2_2, 200, "magenta", false, false, true);
        drawLine2(Pathangle2_2 + Math.PI, 200, "magenta", true, false, false);

        // Delta arc
        ctx2.beginPath();
        ctx2.arc(
          centerX2,
          centerY2,
          radius2 + 20,
          FtPstartAngle,
          FtPendAngle,
          FtPDir
        );
        ctx2.strokeStyle = "orange";
        ctx2.lineWidth = 5;
        ctx2.setLineDash([]);
        ctx2.stroke();

        ctx2.beginPath();
        ctx2.moveTo(centerX2, centerY2);
        ctx2.arc(
          centerX2,
          centerY2,
          radius2 + 20,
          FtPstartAngle,
          FtPendAngle,
          FtPDir
        );
        ctx2.lineTo(centerX2, centerY2);
        ctx2.closePath();
        ctx2.fillStyle = lighterLightOrange;
        ctx2.fill();
      } else {
        ctx2.fillStyle = "#00000049"
        ctx2.fillRect(0, 0, canvas2.width, canvas2.height);

        ctx2.fillStyle = "#ffffffff";
        ctx2.font = "bold 24px 'Kodchasan'";
        ctx2.textAlign = "center";
        ctx2.fillText("No Face, Path", centerX2, centerY2);
      }
    };

    img2.src = clubImage;

  }, [selectedShot, selectedSetup]);

  return (
    <>
      <canvas
        ref={canvasRef}
        width={400}
        height={300}
      />
    </>
  );
}

export default FacePath;
